import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { createTeam } from '../../../services/teamService';
import { createDeliverable } from '../../../services/deliverableService';

const CreateTeamModal = ({ isOpen, onClose, onTeamCreated }) => {
  const [formData, setFormData] = useState({
    teamName: '',
    teamNumber: '',
    problemStatement: '',
    mentor: '',
    mentorEmail: '',
    city: '',
    category: '',
    projectDeadline: '',
    deliverables: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddDeliverable = () => {
    setFormData(prev => ({
      ...prev,
      deliverables: [
        ...prev?.deliverables,
        {
          id: `temp-${Date.now()}`,
          deliverableName: '',
          dueDate: '',
          requiredUploadCount: 1
        }
      ]
    }));
  };

  const handleRemoveDeliverable = (tempId) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev?.deliverables?.filter(d => d?.id !== tempId)
    }));
  };

  const handleDeliverableChange = (tempId, field, value) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev?.deliverables?.map(d => 
        d?.id === tempId ? { ...d, [field]: value } : d
      )
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!formData?.teamName?.trim()) {
      setError('Team name is required');
      return;
    }
    if (!formData?.teamNumber) {
      setError('Team number is required');
      return;
    }
    if (!formData?.problemStatement?.trim()) {
      setError('Problem statement is required');
      return;
    }
    if (!formData?.mentor?.trim()) {
      setError('Manager is required');
      return;
    }
    if (!formData?.city?.trim()) {
      setError('City is required');
      return;
    }
    if (!formData?.category?.trim()) {
      setError('Category is required');
      return;
    }
    if (!formData?.projectDeadline) {
      setError('Project deadline is required');
      return;
    }

    // Validate deliverables
    for (const deliverable of formData?.deliverables || []) {
      if (!deliverable?.deliverableName?.trim()) {
        setError('All deliverables must have a name');
        return;
      }
      if (!deliverable?.dueDate) {
        setError('All deliverables must have a due date');
        return;
      }
      if (!deliverable?.requiredUploadCount || deliverable?.requiredUploadCount < 1) {
        setError('All deliverables must have at least 1 required upload');
        return;
      }
    }

    try {
      setLoading(true);
      
      // Create team without stage requirements
      const newTeam = await createTeam({
        teamName: formData?.teamName,
        teamNumber: formData?.teamNumber,
        problemStatement: formData?.problemStatement,
        mentor: formData?.mentor,
        mentorEmail: formData?.mentorEmail,
        city: formData?.city,
        category: formData?.category,
        projectDeadline: formData?.projectDeadline
      });

      // Create deliverables for the team
      for (const deliverable of formData?.deliverables || []) {
        await createDeliverable({
          teamId: newTeam?.id,
          deliverableName: deliverable?.deliverableName,
          dueDate: deliverable?.dueDate,
          requiredUploadCount: parseInt(deliverable?.requiredUploadCount) || 1
        });
      }

      onTeamCreated(newTeam);
      
      // Reset form
      setFormData({
        teamName: '',
        teamNumber: '',
        problemStatement: '',
        mentor: '',
        mentorEmail: '',
        city: '',
        category: '',
        projectDeadline: '',
        deliverables: []
      });
    } catch (err) {
      console.error('Error creating team:', err);
      setError(err?.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground">Create New Team</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Fill in the details to create a new team
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">Basic Information</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Team Name"
                required
                value={formData?.teamName}
                onChange={(e) => handleInputChange('teamName', e?.target?.value)}
                placeholder="Enter team name"
              />
              
              <Input
                label="Team Number"
                type="number"
                required
                value={formData?.teamNumber}
                onChange={(e) => handleInputChange('teamNumber', e?.target?.value)}
                placeholder="Enter team number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Problem Statement <span className="text-error">*</span>
              </label>
              <textarea
                required
                value={formData?.problemStatement}
                onChange={(e) => handleInputChange('problemStatement', e?.target?.value)}
                placeholder="Describe the problem this team will address..."
                className="w-full min-h-[100px] p-3 rounded-lg border border-border bg-background text-foreground resize-y"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <Input
                label="Manager"
                required
                value={formData?.mentor}
                onChange={(e) => handleInputChange('mentor', e?.target?.value)}
                placeholder="Enter manager name"
              />
              
              <Input
                label="Manager's Email"
                type="email"
                value={formData?.mentorEmail}
                onChange={(e) => handleInputChange('mentorEmail', e?.target?.value)}
                placeholder="Enter manager's email"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                required
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                placeholder="Enter city"
              />
              
              <Input
                label="Category"
                required
                value={formData?.category}
                onChange={(e) => handleInputChange('category', e?.target?.value)}
                placeholder="e.g., Transportation, Environment"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Project Deadline"
                type="date"
                value={formData?.projectDeadline}
                onChange={(e) => handleInputChange('projectDeadline', e?.target?.value)}
              />
            </div>
          </div>

          {/* Deliverables Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-foreground">Deliverables</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddDeliverable}
                disabled={loading}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Add Deliverable
              </Button>
            </div>

            {formData?.deliverables?.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                <Icon name="FileText" size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No deliverables added yet</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Add Deliverable" to create one</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData?.deliverables?.map((deliverable) => (
                  <div key={deliverable?.id} className="p-4 border border-border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input
                          label="Deliverable Name"
                          required
                          value={deliverable?.deliverableName}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'deliverableName', e?.target?.value)}
                          placeholder="e.g., Problem Analysis"
                        />
                        <Input
                          label="Due Date"
                          type="date"
                          required
                          value={deliverable?.dueDate}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'dueDate', e?.target?.value)}
                        />
                        <Input
                          label="Required Upload Count"
                          type="number"
                          min="1"
                          required
                          value={deliverable?.requiredUploadCount}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'requiredUploadCount', e?.target?.value)}
                          placeholder="1"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDeliverable(deliverable?.id)}
                        className="ml-2 p-2 hover:bg-error/10 rounded-lg transition-colors"
                        disabled={loading}
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeamModal;